import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { GetAccessTokenUseCase } from '@auth';
import { AppConfigService } from '../config/app-config.service';
import { EIDCProvider } from '../providers/oidc/eidc.provider';
import { LoggerProvider } from '@shared';

/**
 * Token interceptor.
 * Adds the access token and product code to outgoing HTTP requests.
 * In local environments, uses the token from local login (api/local/login).
 * In non-local environments, uses the EID OIDC access token.
 */
export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const getAccessTokenUseCase = inject(GetAccessTokenUseCase);
  const configService = inject(AppConfigService);
  const eidcProvider = inject(EIDCProvider);
  const logger = inject(LoggerProvider).createScoped('TokenInterceptor');

  // Skip adding headers for non-API requests and auth endpoints
  const skipTokenUrls = ['/assets/config.json', '/api/auth/login', '/api/auth/refresh', '/api/auth/logout', '/api/local/login'];
  const shouldSkipToken = skipTokenUrls.some((url) => req.url.includes(url));

  if (shouldSkipToken) {
    logger.debug('Skipping token for URL', { url: req.url });
    return next(req);
  }

  const headers: Record<string, string> = {};

  // Add product code header for API identification
  headers['productCode'] = configService.productCode;

  // Determine access token based on environment
  if (configService.isLocal()) {
    // Local: use token obtained from api/local/login
    const accessToken = getAccessTokenUseCase.execute()();
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      logger.debug('Added local auth token to request', { url: req.url, hasToken: true });
    } else {
      logger.warn('No local token available for request', { url: req.url });
    }
  } else {
    // Non-local (EID): Try EID provider first, fallback to AuthState
    let accessToken = eidcProvider.accessToken();

    // If EID provider doesn't have token, try getting it from AuthState
    // This handles cases where bridgeAuthState synced to AuthState but not EIDCProvider
    if (!accessToken) {
      const authStateToken = getAccessTokenUseCase.execute()();
      if (authStateToken) {
        logger.info('EID token not in provider, using AuthState token as fallback', { url: req.url });
        accessToken = authStateToken;
      }
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      logger.debug('Added EID auth token to request', {
        url: req.url,
        hasToken: true,
        source: accessToken === eidcProvider.accessToken() ? 'eidProvider' : 'authState'
      });
    } else {
      logger.warn('No EID token available for request', {
        url: req.url,
        eidAuthenticated: eidcProvider.isAuthenticated(),
        hasEidToken: !!eidcProvider.accessToken(),
        hasAuthStateToken: !!getAccessTokenUseCase.execute()()
      });
    }
  }

  const clonedRequest = req.clone({ setHeaders: headers });
  return next(clonedRequest);
};
